import { useState, useEffect } from 'react';
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, CircularProgress, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AttachementsUpload } from '../../../../shared_elements/components';
import { checkApiStatus, numberWithCommas, getTextFieldRows } from '../../../../utils';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { PageLoader } from '../../../../shared_elements';
import moment from 'moment';
import { updateClaimApi, addClaimApi } from '../apiService';
import { globalPutService, globalPostService } from '../../../../utils/globalApiServices';
import { trackActivity } from '../../../../utils/mixpanel';
import { setFileUploadDetails } from '../../../../shared_elements/actions';
import { useDispatch, useSelector } from 'react-redux';
import { formatBytes } from '../../../../utils/fileUpload';
import LessMoreTextFieldComp from '../../../../shared_elements/components/LessMoreTextFieldComp';
import { regexConstants } from '../../../../constants/regEx';

const ClaimCRU = ({ claimCRU, modeType, fileUpload,getResponseBack, onOpenAttachments, onToggleModal, extraData }) => {
    const dispatch=useDispatch()
    const { leaseSlug } = useParams();
    const { storageDetails } = useSelector((state) => state.leases); 
    const params = { leaseSlug: leaseSlug };
    const { enqueueSnackbar } = useSnackbar()
    const [error, setError] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState(claimCRU?.data);
    const [attachments, setAttachments] = useState([]);
    const [cleared, setCleared] = useState(false);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);
            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [])

    const handleFileChange = (files) => {
        setAttachments(files);
        dispatch(setFileUploadDetails({attachments:files}))
    };

    const onFieldChange = (key, value) => {
        setData({ ...data, [key]: value });
        setError((prev) => ({ ...prev, [key]: '' }));
    };
    const onRestErrorKey = (keyParam) => {
        setError({ ...error, [keyParam]: '' })
    }

    const onAddEditClaim = () => {
        let validationInputs = {};
        validationInputs = {
            claim_number: data?.claim_number?.trim() ? "" : "Please enter Claim Number",
            amount: data?.amount ? "" : "Please enter Amount",
            date_of_claim_closure: data?.date_of_claim_closure ? "" : "Please select Date of Claim Closure",
            claim_details: data?.claim_details?.trim() ? "" : "Please enter Claim Details",
        };
        const dateOfClaimClouser = moment(data?.date_of_claim_closure);
        if(data?.date_of_claim_closure && !dateOfClaimClouser.isValid()) {
            validationInputs.date_of_claim_closure = "Please enter valid Date Of Claim Clouser"
        }

        let payload = Object.assign({}, data)
        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
            setLoading(true);
            if (data?.id) {
                globalPutService(updateClaimApi({ ...params, insuranceSlug: extraData?.slug, slug: data?.slug }), payload).then(async (response) => {
                    if (checkApiStatus(response)) {
                        if (attachments?.length) {
                            fileUpload(data?.slug,'updated')
                        }
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Edited', { page_title: `Edit Claim`, success_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Edited', { page_title: `Edit Claim`, error_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    }
                    setLoading(false);
                });
            } else {
                globalPostService(addClaimApi({ ...params, insuranceSlug: extraData?.slug }), payload).then(async (response) => {
                    if (checkApiStatus(response)) {
                        if (attachments?.length) {
                            fileUpload(response?.data?.data?.slug,'created')
                        }
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Added', { page_title: `Add Claim`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Added', { page_title: `Add Claim`, error_msg: response?.data?.message, item_id: null, payload: data, response: response?.data })
                    }
                });
            }
        } else {
            setError(validationInputs);
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Claim`, error_msg: validationInputs, })
        }
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Claim`, page_type: page_type, })
    }

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth='md'
                fullWidth
                open={true}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
                    <Typography sx={{ marginLeft: '10px' }}>
                        {modeType === 'add' ? 'Add Claim' : modeType === 'edit' ? 'Edit Claim' : 'View Claim'}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="insurance_number"
                                label="Insurance Number"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={extraData?.insurance_number || ''}
                                disabled
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="claim_number"
                                label="Claim Number"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                error={!!error.claim_number}
                                helperText={error.claim_number}
                                value={data?.claim_number || ''}
                                onChange={(e) => { if (e.target.value?.length <= 50) { onFieldChange("claim_number", e.target.value); } }}
                                disabled={modeType === 'view'}
                                onFocus={() => onRestErrorKey("claim_number")}
                            />
                        </Grid>

                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="amount"
                                label="Amount "
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                error={!!error.amount}
                                helperText={error.amount}
                                value={modeType === 'view' && data?.amount ? numberWithCommas(data?.amount) : (data?.amount || '')}
                                disabled={modeType === 'view'}
                                onFocus={() => onRestErrorKey("amount")}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") { onFieldChange("amount", ""); return; }
                                    if (val?.length <= 20 && regexConstants.decimalnodotstart.test(val)) {
                                        onFieldChange("amount", val);
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Date of Claim Closure"
                                    minDate={moment(extraData?.policy_start_date)}
                                    maxDate={moment(extraData?.policy_end_date)}
                                    value={data?.date_of_claim_closure ? moment(data.date_of_claim_closure) : null}
                                    // onChange={(data) => onFieldChange("date_of_claim_closure", moment(data)?.isValid() ? moment(data).format(backendDateFormat) : null)}
                                    onChange={(data) => { onFieldChange("date_of_claim_closure", data ? moment(data).format(backendDateFormat) : null) }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.date_of_claim_closure || false,
                                            helperText: error.date_of_claim_closure || '',
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("date_of_claim_closure", null);
                                                setCleared(true);
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="primary_contact"
                                label="Primary Contact "
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={data?.primary_contact || ''}
                                onChange={(e) => { if (e.target.value?.length <= 255) { onFieldChange("primary_contact", e.target.value); } }}
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <TextField
                                required
                                id="claim_details"
                                label="Claim Details"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 255} }}
                                fullWidth
                                multiline
                                rows={getTextFieldRows(data?.claim_details, 3, 100)}
                                error={!!error.claim_details}
                                helperText={error.claim_details}
                                value={data?.claim_details || ''}
                                onChange={(e) =>  { onFieldChange("claim_details", e.target.value); } }
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        {modeType === 'view' ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <LessMoreTextFieldComp label='Remarks' value={data?.remarks || ""} />
                            </Grid>
                            : <Grid size={{ md: 12, xs: 12 }}>
                                <TextField
                                    id="remarks"
                                    label="Remarks"
                                    variant="outlined"
                                    size='small'
                                    margin='normal'
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={data?.remarks || ''}
                                    onChange={(e) => { onFieldChange("remarks", e.target.value); }}
                                    disabled={modeType === 'view'}
                                />
                            </Grid>}
                        <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>
                            <Box sx={{display:"flex",justifyContent:"space-between"}} >
                            {data?.id && data?.attachments?.length ?
                                <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({claimCRU?.data?.attachments?.length})Attachment(s)</Button> : null
                            }
                            <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary'>{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button>
                            </Box>
                            {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className="dialog-action">
                    <Button className='cancel-btn' variant='outlined' disabled={isLoading} size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
                    {modeType === "view" ? null : <Button variant='contained' size='small' className='btn-primary' disabled={modeType === 'view'} onClick={() => { handleItemClicked('Save Button'); onAddEditClaim() }}>{isLoading ? <CircularProgress color='inherit' size={25} /> : "SAVE"}</Button>}
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>

    )
}
export default ClaimCRU